/**
 * Blackjack — server-authoritative rules engine (2–5 players against the dealer, session game with chips).
 *
 * Rules implemented (toggles live in {@link BlackjackConfig}):
 *  - A shoe of `decks` decks, reshuffled when a quarter is left. Players bet in seat order (or sit out), then everyone
 *    gets two cards and the dealer one up, one down. If the dealer shows a ten or ace they peek: a dealer blackjack
 *    ends the hand at once (player blackjacks push, everything else loses).
 *  - Players act in turn: hit, stand, double (one card, bet doubled), or split a pair (same rank, up to `maxSplits`
 *    splits; split aces get one card each and cannot be resplit). Doubling after a split is a toggle.
 *  - Twenty-one stands automatically. A blackjack (ace + ten-value on the first two cards) pays 3:2 (or 6:5 when
 *    configured); other wins pay 1:1; a push returns the bet. A hand made after a split is never a blackjack.
 *  - The dealer reveals and draws to 17, standing on soft 17 unless `dealerHitsSoft17`.
 *  - No insurance, surrender or side bets.
 */
import { buildDeck, parseCard, type CardId } from '../../core/cards.js';
import { checkBool, checkEnum, checkInt, mergeKnown } from '../../core/config.js';
import { shuffle, type Rng } from '../../core/rng.js';
import {
  assertEngine,
  EngineError,
  isPlainObject,
  standingsFromScores,
  type EngineEvent,
  type GameEngine,
  type SeatSetup,
} from '../../core/types.js';

export interface BlackjackConfig {
  minBet: number;
  maxBet: number;
  decks: number;
  minPlayers: number;
  maxPlayers: number;
  dealerHitsSoft17: boolean;
  doubleAfterSplit: boolean;
  /** Splits allowed per player per hand (so up to maxSplits + 1 hands). */
  maxSplits: number;
  blackjackPays: '3:2' | '6:5';
  startDelayMs: number;
  dealDelayMs: number;
  dealerDelayMs: number;
  handEndDelayMs: number;
}

export type BlackjackPhase = 'WAITING' | 'BETTING' | 'DEALING' | 'PLAYING' | 'DEALER' | 'HAND_END';
export type BlackjackSeatStatus = 'WAITING' | 'BET' | 'SIT_OUT';
export type BlackjackResult = 'BLACKJACK' | 'WIN' | 'PUSH' | 'LOSE' | 'BUST';

export interface BlackjackHand {
  cards: CardId[];
  bet: number;
  done: boolean;
  bust: boolean;
  /** A natural: ace + ten-value on the first two cards of an unsplit hand. */
  blackjack: boolean;
  doubled: boolean;
  fromSplit: boolean;
  result: BlackjackResult | null;
  payout: number;
}

export interface BlackjackPlayer {
  playerId: string;
  chips: number;
  status: BlackjackSeatStatus;
  hands: BlackjackHand[];
}

export interface BlackjackSeatResult {
  seat: number;
  delta: number;
  hands: { cards: CardId[]; bet: number; result: BlackjackResult; payout: number }[];
}

export interface BlackjackHandResult {
  handNo: number;
  dealer: CardId[];
  dealerTotal: number;
  dealerBlackjack: boolean;
  seats: BlackjackSeatResult[];
}

export interface BlackjackState {
  config: BlackjackConfig;
  seats: (BlackjackPlayer | null)[];
  phase: BlackjackPhase;
  handNo: number;
  /** Seat to act (betting or playing) and which of its hands. */
  turn: number;
  activeHand: number;
  shoe: CardId[];
  dealer: CardId[];
  dealerRevealed: boolean;
  dealerBlackjack: boolean;
  lastResult: BlackjackHandResult | null;
  lastAction: { seat: number; type: string; hand?: number } | null;
  handsPlayed: number;
}

export type BlackjackAction =
  | { type: 'bet'; amount: number }
  | { type: 'sitOut' }
  | { type: 'hit' }
  | { type: 'stand' }
  | { type: 'double' }
  | { type: 'split' };

export type BlackjackLegal =
  | { kind: 'bet'; min: number; max: number; canSitOut: boolean }
  | { kind: 'play'; hit: boolean; stand: boolean; double: boolean; split: boolean }
  | { kind: 'none' };

export interface BlackjackViewHand extends BlackjackHand {
  total: number;
  soft: boolean;
}

export interface BlackjackView {
  gameType: 'BLACKJACK';
  config: BlackjackConfig;
  seat: number | null;
  phase: BlackjackPhase;
  handNo: number;
  turn: number;
  activeHand: number;
  /** Dealer cards; the hole card is null until it is revealed. */
  dealer: (CardId | null)[];
  dealerTotal: number | null;
  dealerRevealed: boolean;
  shoeCount: number;
  players: ({ seat: number; playerId: string; chips: number; status: BlackjackSeatStatus; hands: BlackjackViewHand[] } | null)[];
  lastResult: BlackjackHandResult | null;
  lastAction: { seat: number; type: string; hand?: number } | null;
}

const DEFAULTS: BlackjackConfig = {
  minBet: 10,
  maxBet: 1000,
  decks: 6,
  minPlayers: 2,
  maxPlayers: 5,
  dealerHitsSoft17: false,
  doubleAfterSplit: true,
  maxSplits: 3,
  blackjackPays: '3:2',
  startDelayMs: 2000,
  dealDelayMs: 800,
  dealerDelayMs: 1000,
  handEndDelayMs: 4000,
};

const cardValue = (c: CardId): number => {
  const r = parseCard(c).rank;
  return r === 14 ? 11 : Math.min(r, 10);
};

/** Best total of a hand, and whether an ace is still counted as 11. */
export function blackjackValue(cards: readonly CardId[]): { total: number; soft: boolean } {
  let total = 0;
  let aces = 0;
  for (const c of cards) {
    total += cardValue(c);
    if (parseCard(c).rank === 14) aces++;
  }
  while (total > 21 && aces > 0) {
    total -= 10;
    aces--;
  }
  return { total, soft: aces > 0 };
}

export const isBlackjack = (cards: readonly CardId[]): boolean => cards.length === 2 && blackjackValue(cards).total === 21;

/** Counting value of the dealer's upcard (ace 11). */
export const blackjackUpValue = (card: CardId): number => cardValue(card);

function seatsWhere(s: BlackjackState, pred: (p: BlackjackPlayer, i: number) => boolean): number[] {
  const out: number[] = [];
  s.seats.forEach((p, i) => {
    if (p && pred(p, i)) out.push(i);
  });
  return out;
}

const canStart = (s: BlackjackState): boolean => seatsWhere(s, (p) => p.chips >= s.config.minBet).length >= s.config.minPlayers;

function draw(s: BlackjackState, rng: Rng): CardId {
  if (s.shoe.length === 0) {
    // Out of cards mid-hand: reshuffle everything not on the table.
    const onTable = new Set<CardId>(s.dealer);
    for (const p of s.seats) if (p) for (const h of p.hands) for (const c of h.cards) onTable.add(c);
    s.shoe = shuffle(
      buildDeck({ decks: s.config.decks }).filter((c) => !onTable.has(c)),
      rng,
    );
  }
  return s.shoe.pop()!;
}

/** Legal moves for the hand a seat is playing. */
function playLegal(s: BlackjackState, seat: number): Extract<BlackjackLegal, { kind: 'play' }> {
  const p = s.seats[seat]!;
  const h = p.hands[s.activeHand];
  if (!h || h.done) return { kind: 'play', hit: false, stand: false, double: false, split: false };
  const two = h.cards.length === 2;
  const funds = p.chips >= h.bet;
  const canDouble = two && funds && (!h.fromSplit || s.config.doubleAfterSplit);
  const canSplit = two && funds && p.hands.length <= s.config.maxSplits && parseCard(h.cards[0]!).rank === parseCard(h.cards[1]!).rank && !(h.fromSplit && parseCard(h.cards[0]!).rank === 14);
  return { kind: 'play', hit: true, stand: true, double: canDouble, split: canSplit };
}

/** Moves to the next hand that still needs a decision, or to the dealer. */
function advance(s: BlackjackState): void {
  const cur = s.seats[s.turn];
  if (cur) {
    for (let i = s.activeHand + 1; i < cur.hands.length; i++) {
      if (!cur.hands[i]!.done) {
        s.activeHand = i;
        return;
      }
    }
  }
  for (let seat = s.turn + 1; seat < s.seats.length; seat++) {
    const p = s.seats[seat];
    const idx = p ? p.hands.findIndex((h) => !h.done) : -1;
    if (p && idx >= 0) {
      s.turn = seat;
      s.activeHand = idx;
      return;
    }
  }
  s.phase = 'DEALER';
}

function startPlaying(s: BlackjackState): void {
  s.phase = 'PLAYING';
  s.turn = -1;
  s.activeHand = 0;
  advance(s);
}

function advanceBetting(s: BlackjackState, from: number): void {
  for (let seat = from + 1; seat < s.seats.length; seat++) {
    const p = s.seats[seat];
    if (p && p.status === 'WAITING') {
      s.turn = seat;
      return;
    }
  }
  s.phase = seatsWhere(s, (p) => p.status === 'BET').length > 0 ? 'DEALING' : 'WAITING';
}

function startHand(s: BlackjackState, rng: Rng, events: EngineEvent[]): void {
  const cfg = s.config;
  s.handNo++;
  s.dealer = [];
  s.dealerRevealed = false;
  s.dealerBlackjack = false;
  s.lastAction = null;
  if (s.shoe.length < cfg.decks * 52 * 0.25) {
    s.shoe = shuffle(buildDeck({ decks: cfg.decks }), rng);
    events.push({ type: 'shuffle', cards: s.shoe.length });
  }
  for (const p of s.seats) {
    if (!p) continue;
    p.hands = [];
    p.status = p.chips >= cfg.minBet ? 'WAITING' : 'SIT_OUT';
  }
  s.phase = 'BETTING';
  s.turn = -1;
  events.push({ type: 'handStart', handNo: s.handNo });
  advanceBetting(s, -1);
  if (s.phase !== 'BETTING') events.push({ type: 'waitingForPlayers' });
}

function settle(s: BlackjackState, events: EngineEvent[]): void {
  const dealerVal = blackjackValue(s.dealer).total;
  const dealerBust = dealerVal > 21;
  const results: BlackjackSeatResult[] = [];
  s.seats.forEach((p, seat) => {
    if (!p || p.hands.length === 0) return;
    let delta = 0;
    const hands = p.hands.map((h) => {
      let result: BlackjackResult;
      let payout = 0;
      const total = blackjackValue(h.cards).total;
      if (h.bust) result = 'BUST';
      else if (h.blackjack && s.dealerBlackjack) result = 'PUSH';
      else if (h.blackjack) result = 'BLACKJACK';
      else if (s.dealerBlackjack) result = 'LOSE';
      else if (dealerBust || total > dealerVal) result = 'WIN';
      else if (total === dealerVal) result = 'PUSH';
      else result = 'LOSE';
      if (result === 'BLACKJACK') payout = h.bet + (s.config.blackjackPays === '3:2' ? Math.floor((h.bet * 3) / 2) : Math.floor((h.bet * 6) / 5));
      else if (result === 'WIN') payout = h.bet * 2;
      else if (result === 'PUSH') payout = h.bet;
      h.result = result;
      h.payout = payout;
      p.chips += payout;
      delta += payout - h.bet;
      return { cards: [...h.cards], bet: h.bet, result, payout };
    });
    results.push({ seat, delta, hands });
  });
  s.dealerRevealed = true;
  s.lastResult = { handNo: s.handNo, dealer: [...s.dealer], dealerTotal: dealerVal, dealerBlackjack: s.dealerBlackjack, seats: results };
  s.handsPlayed++;
  s.phase = 'HAND_END';
  events.push({ type: 'handEnd', handNo: s.handNo, dealerTotal: dealerVal, results: results.map((r) => ({ seat: r.seat, delta: r.delta })) });
}

export const blackjackEngine: GameEngine<BlackjackConfig, BlackjackState, BlackjackAction, BlackjackView, BlackjackLegal> = {
  gameType: 'BLACKJACK',
  kind: 'session',

  defaultConfig: () => ({ ...DEFAULTS }),

  normalizeConfig(input) {
    const c = mergeKnown(DEFAULTS, input);
    checkInt('maxPlayers', c.maxPlayers, 1, 7);
    checkInt('minPlayers', c.minPlayers, 1, c.maxPlayers);
    checkInt('minBet', c.minBet, 1, 1e9);
    checkInt('maxBet', c.maxBet, c.minBet, 1e12);
    checkInt('decks', c.decks, 1, 8);
    checkBool('dealerHitsSoft17', c.dealerHitsSoft17);
    checkBool('doubleAfterSplit', c.doubleAfterSplit);
    checkInt('maxSplits', c.maxSplits, 0, 3);
    checkEnum('blackjackPays', c.blackjackPays, ['3:2', '6:5'] as const);
    checkInt('startDelayMs', c.startDelayMs, 0, 60000);
    checkInt('dealDelayMs', c.dealDelayMs, 0, 60000);
    checkInt('dealerDelayMs', c.dealerDelayMs, 0, 60000);
    checkInt('handEndDelayMs', c.handEndDelayMs, 0, 60000);
    return c;
  },

  seatLimits: (c) => ({ min: c.minPlayers, max: c.maxPlayers }),

  createState(config, seats, rng) {
    const s: BlackjackState = {
      config,
      seats: Array<BlackjackPlayer | null>(config.maxPlayers).fill(null),
      phase: 'WAITING',
      handNo: 0,
      turn: -1,
      activeHand: 0,
      shoe: shuffle(buildDeck({ decks: config.decks }), rng),
      dealer: [],
      dealerRevealed: false,
      dealerBlackjack: false,
      lastResult: null,
      lastAction: null,
      handsPlayed: 0,
    };
    for (const setup of seats) this.addSeat!(s, setup);
    return s;
  },

  addSeat(s, setup: SeatSetup) {
    assertEngine(setup.seat >= 0 && setup.seat < s.seats.length && !s.seats[setup.seat], 'SEAT_TAKEN', 'Seat unavailable');
    const chips = setup.chips ?? 0;
    assertEngine(Number.isInteger(chips) && chips >= 0, 'INVALID_AMOUNT', 'Invalid chips');
    // Anyone joining once cards are out waits for the next hand; while bets are still being taken a later seat can join in.
    const open = s.phase === 'WAITING' || s.phase === 'HAND_END' || (s.phase === 'BETTING' && chips >= s.config.minBet);
    s.seats[setup.seat] = { playerId: setup.playerId, chips, status: open ? 'WAITING' : 'SIT_OUT', hands: [] };
    return [{ type: 'playerJoined', seat: setup.seat, playerId: setup.playerId, chips }];
  },

  removeSeat(s, seat) {
    const p = s.seats[seat];
    assertEngine(p, 'INVALID_ACTION', 'Seat empty');
    const events: EngineEvent[] = [];
    let chips = p.chips;
    const wasTurn = s.turn === seat;
    if (s.phase === 'BETTING') {
      // Nothing is dealt yet: the bet comes back.
      chips += p.hands.reduce((n, h) => n + h.bet, 0);
      p.hands = [];
      s.seats[seat] = null;
      if (wasTurn) advanceBetting(s, seat);
      else if (seatsWhere(s, (x) => x.status === 'WAITING').length === 0 && s.phase === 'BETTING') advanceBetting(s, s.seats.length);
    } else if (s.phase === 'DEALING' || s.phase === 'PLAYING' || s.phase === 'DEALER') {
      // Walking away mid-hand forfeits the bets on the table.
      s.seats[seat] = null;
      if (s.phase === 'PLAYING' && wasTurn) advance(s);
      else if (s.phase === 'PLAYING' && seatsWhere(s, (x) => x.hands.some((h) => !h.done)).length === 0) s.phase = 'DEALER';
    } else {
      s.seats[seat] = null;
    }
    events.push({ type: 'playerLeft', seat });
    return { chips, events };
  },

  parseAction(raw) {
    if (!isPlainObject(raw)) throw new EngineError('INVALID_ACTION', 'Action must be an object');
    switch (raw.type) {
      case 'bet':
        if (typeof raw.amount === 'number' && Number.isInteger(raw.amount)) return { type: 'bet', amount: raw.amount };
        throw new EngineError('INVALID_AMOUNT', 'Bet must be a whole number');
      case 'sitOut':
      case 'hit':
      case 'stand':
      case 'double':
      case 'split':
        return { type: raw.type };
      default:
        throw new EngineError('INVALID_ACTION', 'Unknown Blackjack action');
    }
  },

  applyAction(s, seat, action, rng) {
    const p = s.seats[seat];
    assertEngine(p, 'INVALID_ACTION', 'You are not at this table');
    assertEngine(s.phase === 'BETTING' || s.phase === 'PLAYING', 'INVALID_PHASE', 'No decision to make right now');
    assertEngine(seat === s.turn, 'NOT_YOUR_TURN', 'Not your turn');
    const events: EngineEvent[] = [];

    if (s.phase === 'BETTING') {
      if (action.type === 'sitOut') {
        p.status = 'SIT_OUT';
        events.push({ type: 'sitOut', seat });
        advanceBetting(s, seat);
        return events;
      }
      assertEngine(action.type === 'bet', 'INVALID_PHASE', 'Place a bet first');
      const max = Math.min(s.config.maxBet, p.chips);
      assertEngine(action.amount >= s.config.minBet && action.amount <= max, 'INVALID_AMOUNT', `Bet between ${s.config.minBet} and ${max}`);
      p.chips -= action.amount;
      p.status = 'BET';
      p.hands = [{ cards: [], bet: action.amount, done: false, bust: false, blackjack: false, doubled: false, fromSplit: false, result: null, payout: 0 }];
      events.push({ type: 'bet', seat, amount: action.amount });
      advanceBetting(s, seat);
      return events;
    }

    assertEngine(action.type !== 'bet' && action.type !== 'sitOut', 'INVALID_PHASE', 'Betting is closed');
    const h = p.hands[s.activeHand]!;
    const legal = playLegal(s, seat);
    s.lastAction = { seat, type: action.type, hand: s.activeHand };

    if (action.type === 'stand') {
      assertEngine(legal.stand, 'ILLEGAL_MOVE', 'You cannot stand now');
      h.done = true;
      events.push({ type: 'stand', seat, hand: s.activeHand });
      advance(s);
      return events;
    }

    if (action.type === 'hit') {
      assertEngine(legal.hit, 'ILLEGAL_MOVE', 'You cannot hit now');
      const card = draw(s, rng);
      h.cards.push(card);
      const total = blackjackValue(h.cards).total;
      events.push({ type: 'hit', seat, hand: s.activeHand, card });
      if (total > 21) {
        h.bust = true;
        h.done = true;
      } else if (total === 21) {
        h.done = true;
      }
      if (h.done) advance(s);
      return events;
    }

    if (action.type === 'double') {
      assertEngine(legal.double, 'ILLEGAL_MOVE', 'You cannot double now');
      p.chips -= h.bet;
      h.bet *= 2;
      h.doubled = true;
      const card = draw(s, rng);
      h.cards.push(card);
      if (blackjackValue(h.cards).total > 21) h.bust = true;
      h.done = true;
      events.push({ type: 'double', seat, hand: s.activeHand, card });
      advance(s);
      return events;
    }

    assertEngine(legal.split, 'ILLEGAL_MOVE', 'You cannot split now');
    const [first, second] = h.cards as [CardId, CardId];
    const aces = parseCard(first).rank === 14;
    p.chips -= h.bet;
    const other: BlackjackHand = { cards: [second], bet: h.bet, done: false, bust: false, blackjack: false, doubled: false, fromSplit: true, result: null, payout: 0 };
    h.cards = [first];
    h.fromSplit = true;
    p.hands.splice(s.activeHand + 1, 0, other);
    for (const x of [h, other]) {
      x.cards.push(draw(s, rng));
      if (aces || blackjackValue(x.cards).total === 21) x.done = true;
    }
    events.push({ type: 'split', seat, hand: s.activeHand });
    if (h.done) advance(s);
    return events;
  },

  getActors(s) {
    return (s.phase === 'BETTING' || s.phase === 'PLAYING') && s.turn >= 0 && s.seats[s.turn] ? [s.turn] : [];
  },

  getLegalActions(s, seat) {
    if (seat !== s.turn || !s.seats[seat]) return { kind: 'none' };
    if (s.phase === 'BETTING') {
      const p = s.seats[seat]!;
      return { kind: 'bet', min: s.config.minBet, max: Math.min(s.config.maxBet, p.chips), canSitOut: true };
    }
    if (s.phase === 'PLAYING') return playLegal(s, seat);
    return { kind: 'none' };
  },

  getTimeoutAction(s) {
    return s.phase === 'BETTING' ? { type: 'sitOut' } : { type: 'stand' };
  },

  getView(s, seat) {
    const shown = s.dealerRevealed || s.phase === 'HAND_END';
    return {
      gameType: 'BLACKJACK',
      config: s.config,
      seat,
      phase: s.phase,
      handNo: s.handNo,
      turn: s.turn,
      activeHand: s.activeHand,
      dealer: s.dealer.map((c, i) => (i === 1 && !shown ? null : c)),
      dealerTotal: s.dealer.length === 0 ? null : shown ? blackjackValue(s.dealer).total : blackjackValue(s.dealer.slice(0, 1)).total,
      dealerRevealed: shown,
      shoeCount: s.shoe.length,
      players: s.seats.map((p, i) =>
        p
          ? {
              seat: i,
              playerId: p.playerId,
              chips: p.chips,
              status: p.status,
              hands: p.hands.map((h) => ({ ...h, cards: [...h.cards], ...blackjackValue(h.cards) })),
            }
          : null,
      ),
      lastResult: s.lastResult ? structuredClone(s.lastResult) : null,
      lastAction: s.lastAction ? { ...s.lastAction } : null,
    };
  },

  getSystemStep(s) {
    if (s.phase === 'DEALING') return { kind: 'deal', delayMs: s.config.dealDelayMs };
    if (s.phase === 'DEALER') return { kind: 'dealer', delayMs: s.config.dealerDelayMs };
    if (s.phase === 'HAND_END') return { kind: 'nextHand', delayMs: s.config.handEndDelayMs };
    if (s.phase === 'WAITING' && canStart(s)) return { kind: 'startHand', delayMs: s.config.startDelayMs };
    return null;
  },

  runSystemStep(s, rng) {
    const events: EngineEvent[] = [];
    if (s.phase === 'DEALING') {
      const bettors = seatsWhere(s, (p) => p.status === 'BET');
      for (let round = 0; round < 2; round++) {
        for (const seat of bettors) s.seats[seat]!.hands[0]!.cards.push(draw(s, rng));
        s.dealer.push(draw(s, rng));
      }
      for (const seat of bettors) {
        const h = s.seats[seat]!.hands[0]!;
        if (isBlackjack(h.cards)) {
          h.blackjack = true;
          h.done = true;
        }
      }
      events.push({ type: 'dealt', dealerUp: s.dealer[0] });
      const up = cardValue(s.dealer[0]!);
      if ((up === 10 || up === 11) && isBlackjack(s.dealer)) {
        // The dealer peeks: a natural ends the hand immediately.
        s.dealerBlackjack = true;
        for (const seat of bettors) for (const h of s.seats[seat]!.hands) h.done = true;
        events.push({ type: 'dealerBlackjack' });
        s.phase = 'DEALER';
      } else {
        startPlaying(s);
      }
      return events;
    }
    if (s.phase === 'DEALER') {
      s.dealerRevealed = true;
      events.push({ type: 'dealerReveal', cards: [...s.dealer] });
      const live = seatsWhere(s, () => true).some((seat) => s.seats[seat]!.hands.some((h) => !h.bust && !h.blackjack));
      if (live && !s.dealerBlackjack) {
        for (;;) {
          const { total, soft } = blackjackValue(s.dealer);
          if (total > 17 || (total === 17 && !(soft && s.config.dealerHitsSoft17))) break;
          const card = draw(s, rng);
          s.dealer.push(card);
          events.push({ type: 'dealerDraw', card });
        }
      }
      settle(s, events);
      return events;
    }
    if (s.phase === 'HAND_END' || s.phase === 'WAITING') {
      s.phase = 'WAITING';
      if (canStart(s)) startHand(s, rng, events);
      else events.push({ type: 'waitingForPlayers' });
    }
    return events;
  },

  isFinished: () => false,
  isHandInProgress: (s) => s.phase === 'DEALING' || s.phase === 'PLAYING' || s.phase === 'DEALER',
  getChips: (s) => Object.fromEntries(seatsWhere(s, () => true).map((i) => [i, s.seats[i]!.chips])),
  getStandings: (s) => standingsFromScores(seatsWhere(s, () => true).map((seat) => ({ seat, score: s.seats[seat]!.chips }))),
  getRoundsCompleted: (s) => s.handsPlayed,
};
